import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "./vehiclePricing";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const suvAvecChauffeur: ChauffeurPage = {
  slug: "suv-avec-chauffeur",
  title: "Range Rover avec chauffeur — SUV de luxe",
  subtitle:
    "SUV de luxe avec chauffeur privé : présence, élégance et polyvalence de la ville à l'arrière-pays.",
  keywords: [
    "Range Rover avec chauffeur",
    "SUV de luxe avec chauffeur Cannes",
    "4x4 avec chauffeur Côte d'Azur",
    "chauffeur SUV Nice",
  ],
  hero: {
    title: "Range Rover avec chauffeur sur la Côte d'Azur — SUV de luxe",
    subtitle:
      "L'élégance d'un SUV haut de gamme avec chauffeur privé : présence affirmée, position haute et confort, de la Croisette aux stations de l'arrière-pays.",
    keywords: [
      "Range Rover avec chauffeur",
      "SUV de luxe",
      "4x4 avec chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Range Rover avec chauffeur privé à Cannes",
    badges: ["Jusqu'à 3 passagers", "Présence & élégance", "Polyvalent"],
  },
  benefits: {
    heading: "Pourquoi le SUV de luxe avec chauffeur KS Rent Services",
    items: [
      {
        title: "Présence & élégance",
        description:
          "Le Range Rover impose une présence raffinée : l'arrivée remarquée d'un SUV de prestige, sans ostentation.",
      },
      {
        title: "Position haute & confort",
        description:
          "Une assise dominante, une vue dégagée et un grand confort de suspension : le voyage en hauteur, en toute sérénité.",
      },
      {
        title: "Polyvalence totale",
        description:
          "Aussi à l'aise sur la Croisette que sur les routes de l'arrière-pays et vers les stations : un seul véhicule pour tous vos trajets.",
      },
      {
        title: "Statement discret",
        description:
          "L'alternative au luxe classique de la berline : un style affirmé pour une clientèle qui aime se démarquer.",
      },
    ],
  },
  services: {
    heading: "Prestations du SUV de luxe avec chauffeur",
    services: [
      {
        title: "Arrivées événements",
        text: "Soirées, événements et réceptions : une arrivée stylée et remarquée au volant d'un SUV de prestige.",
      },
      {
        title: "Transferts montagne & stations",
        text: "Liaisons vers les stations et les routes de l'arrière-pays, où le SUV prend tout son sens.",
      },
      {
        title: "Sorties & découvertes",
        text: "Vignobles, villages perchés et arrière-pays niçois : explorez la région avec hauteur et confort.",
      },
      {
        title: "Mise à disposition",
        text: "SUV et chauffeur à l'heure ou à la journée pour vos déplacements et vos envies.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & aéroport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "Le luxe qui s'affirme, en toute polyvalence",
    subheading: "L'alternative SUV à la berline de prestige.",
    paragraphs: [
      "Le Range Rover conjugue l'élégance d'un SUV de luxe et la polyvalence d'un véhicule à l'aise partout : ville, littoral, arrière-pays et stations.",
      "Position de conduite haute, confort de suspension et présence affirmée : un voyage qui se remarque, mené par un chauffeur discret.",
      "Un interlocuteur unique, des forfaits transparents et la même exigence de ponctualité que sur l'ensemble de notre flotte.",
    ],
    bullets: [
      "SUV de luxe jusqu'à 3 passagers",
      "Forfaits aéroport transparents",
      "Disponibilité 24h/24, 7j/7",
    ],
  },
  process: {
    heading: "Comment réserver votre SUV avec chauffeur",
    steps: [
      "Indiquez votre trajet, vos étapes et vos horaires.",
      "Recevez un forfait clair ou une estimation au kilomètre.",
      "Affectation du Range Rover et de votre chauffeur dédié.",
      "Prise en charge stylée, ponctuelle et confortable.",
    ],
  },
  faq: [
    {
      q: "Quel véhicule pour le SUV de luxe avec chauffeur ?",
      a: "Un Range Rover, SUV de prestige pour jusqu'à 3 passagers, idéal pour les arrivées remarquées et les trajets polyvalents ville / arrière-pays.",
    },
    {
      q: "Quelle différence avec la Classe S ?",
      a: "La Classe S est la berline de prestige au confort lounge ; le Range Rover offre une présence SUV, une position haute et une polyvalence supérieure vers l'arrière-pays et les stations.",
    },
    {
      q: "Convient-il aux trajets vers les stations ?",
      a: "Oui : sa polyvalence en fait le véhicule idéal pour les liaisons vers les stations et les routes de l'arrière-pays.",
    },
    vehiclePriceFaq("fr", "CLASS_R", "d'un Range Rover avec chauffeur"),
    {
      q: "Comment réserver ?",
      a: "Indiquez-nous votre trajet et vos horaires : nous confirmons rapidement le Range Rover et votre chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Mercedes Classe S avec chauffeur",
      href: "/chauffeur/classe-s-avec-chauffeur",
    },
    {
      label: "Van avec chauffeur (7 places)",
      href: "/chauffeur/van-avec-chauffeur",
    },
    { label: "Tous nos services chauffeur", href: "/chauffeur" },
    {
      label: "Cortège de mariage à Cannes",
      href: "/chauffeur/voiture-mariage-cannes",
    },
  ],
  cta: {
    title: "Envie d'un SUV de luxe avec chauffeur ?",
    text: "Dites-nous votre trajet — devis rapide pour votre Range Rover avec chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Demander un devis",
  },
  seo: {
    ...vehicleSeo(
      "fr",
      "CLASS_R",
      "Range Rover avec chauffeur, Côte d'Azur",
      "Range Rover avec chauffeur",
    ),
    canonical: "/chauffeur/suv-avec-chauffeur",
  },
};

export default suvAvecChauffeur;
